import * as React from 'react';
import { connect } from 'react-redux';
import history from 'login-utils/history';
import { css, StyleSheet } from 'aphrodite';
import { colors, combineStyles, fonts, media, mixins, Text } from '@blend/consumer-components';
import { generateInnerHtml } from '@blend/shared-react';

import * as deploymentConfig from 'login-utils/deploymentConfig';
import * as mobileUtils from 'login-utils/mobileUtils';
import * as userUtils from 'login-utils/userUtils';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';
import { paneDefaultStyles } from 'login-components/Common/PaneLayout/paneStyles';
import * as globalWrappers from 'login-utils/globalWrappers';
import * as sessionsAPI from 'login-apis/sessionsAPI';
import { leftRightContentPadding } from './LoanOfficerSharedStyles';

const bulletSize = 6;

const ownStyles = StyleSheet.create({
  applyNowButtonWrapper: {
    paddingBottom: 60,
    width: '37%',
    [media.smallView]: {
      paddingBottom: 24,
      paddingTop: 5,
      width: '100%',
    },
  },
  bulletedItem: {
    position: 'relative',
    paddingBottom: 15,
    paddingLeft: 12,
    ':before': {
      backgroundColor: deploymentConfig.getOrganizationColors().action,
      borderRadius: '50%',
      content: mixins.pseudoContent(''),
      display: 'block',
      height: bulletSize,
      left: -10,
      position: 'absolute',
      top: bulletSize,
      width: bulletSize,
    },
    ':last-child': {
      paddingBottom: 0,
    },
  },
  contentWrapper: combineStyles(mixins.padding(0, leftRightContentPadding), {
    ...fonts.Roboto,
    [media.mediumView]: {
      maxWidth: 550,
    },
  }),
  text: {
    [media.mediumView]: {
      left: -9,
      position: 'relative',
    },
  },
  title: {
    color: colors.darkerGray,
    fontSize: 20,
    paddingBottom: 30,
    position: 'relative',
    ':before': combineStyles(mixins.border(1, 'solid', 'black'), {
      content: mixins.pseudoContent(''),
      position: 'absolute',
      width: 40,
      bottom: 20,
    }),
    [media.smallView]: {
      fontSize: 20,
    },
  },
  content: {
    color: colors.darkerGray,
    paddingBottom: 30,
    whiteSpace: 'pre-line',
    [media.smallView]: {
      fontSize: 16,
      paddingBottom: 40,
    },
  },
  description: {
    paddingBottom: 20,
  },
  list: {
    paddingLeft: 10,
  },
});

export interface OwnProps {
  email?: string;
  viewType: string;
}

const useApplyNowExternalRedirect = () => {
  const { enableApplyNowButtonRedirect, applyNowButtonRedirectUrl } = deploymentConfig.get().loanOfficerPages;
  return enableApplyNowButtonRedirect && !!applyNowButtonRedirectUrl;
};

export class LoanOfficerRightPane extends React.Component<OwnProps> {
  applyNowClickHandler = () => {
    const { email } = this.props;
    const config = deploymentConfig.get();
    const { applyNowButtonRedirectUrl } = config.loanOfficerPages;

    // Else case is ignored because the button that references this function is disabled if we reach here
    // istanbul ignore else
    if (useApplyNowExternalRedirect()) {
      sessionsAPI.sendData({
        referrerId: email,
      });
      globalWrappers.getWindow().location.assign(applyNowButtonRedirectUrl);
    } else if (email) {
      history.push(`/signup?referrerId=${encodeURIComponent(email)}`);
    }
  };

  renderApplyNow() {
    const { email } = this.props;
    const isLoggedInUser = !!userUtils.getCurrentUser();
    const haveLOEmail = !!email;

    // People shouldn't be able to create a new loan through this path if they're already logged in,
    // or if the request for LO information hasn't come back. If we don't have LO information yet,
    // we won't be able to assign the loan correctly.
    const disabled = isLoggedInUser || !haveLOEmail;

    return (
      <div className={css(ownStyles.applyNowButtonWrapper)}>
        <PrimaryButton
          disabled={disabled}
          text="Apply Now"
          fontFamily="Roboto"
          data-it="LoanOfficerPage-applyNow"
          onClick={this.applyNowClickHandler}
        />
      </div>
    );
  }

  render() {
    const { viewType } = this.props;
    return (
      <div className={css(ownStyles.contentWrapper)}>
        <div className={css(ownStyles.text)}>
          {viewType === mobileUtils.viewTypes.MOBILE && this.renderApplyNow()}
          <h2 className={css(paneDefaultStyles.title, ownStyles.title)}>What I can help you with</h2>
          <div className={css(ownStyles.content)}>
            <p className={css(ownStyles.description)}>
              My team and I are here to answer any questions you have. I can help you understand more about:
            </p>
            <ul className={css(ownStyles.list)}>
              <li className={css(ownStyles.bulletedItem)}>Loan programs</li>
              <li className={css(ownStyles.bulletedItem)}>Rates</li>
              <li className={css(ownStyles.bulletedItem)}>The loan process</li>
              <li className={css(ownStyles.bulletedItem)}>What you'll need for your application</li>
              <li className={css(ownStyles.bulletedItem)}>What to expect after you complete your application</li>
            </ul>
          </div>
          {viewType !== mobileUtils.viewTypes.MOBILE && this.renderApplyNow()}
          {deploymentConfig.get().loanOfficerPages.organizationDescription && (
            <React.Fragment>
              <h2 className={css(paneDefaultStyles.title, ownStyles.title)}>
                About {deploymentConfig.get().organization.friendlyName}
              </h2>
              <div className={css(ownStyles.content)} data-it="organization-description">
                <Text
                  {...generateInnerHtml(deploymentConfig.get().loanOfficerPages.organizationDescription)}
                  color={colors.darkerGray}
                  size="md"
                />
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }
}

export default connect((state: ReduxState) => ({
  viewType: state.global.viewType,
}))(LoanOfficerRightPane);
